import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, SparklesIcon, UserIcon, PaintbrushIcon, MessageSquareIcon, DatabaseIcon, UserPlusIcon, BrainIcon, PlayIcon, RocketIcon, CheckIcon, XIcon, PlusIcon, CopyIcon, UploadIcon, SendIcon, HeadphonesIcon, LanguagesIcon } from 'lucide-react';
import { WidgetPreview, WidgetConfig } from './WidgetPreview';
const POSITIONS: WidgetConfig['position'][] = ['Bottom Right', 'Bottom Left', 'Floating Center', 'Embedded Page', 'Full Screen Assistant'];
const SIZES: WidgetConfig['size'][] = ['Compact', 'Standard', 'Expanded', 'Full Screen'];
const KNOWLEDGE = ['Inventory', 'Catalog', 'FAQs', 'Business Rules', 'Pricing Rules', 'Knowledge Base', 'Documents', 'Services', 'Policies', 'Terms & Conditions', 'Custom URLs'];
const LEAD_FIELDS = ['Name', 'Email', 'Mobile Number', 'WhatsApp', 'Location', 'Budget', 'Timeline'];
const TRIGGERS = ['Request callback', 'Request quotation', 'Request viewing', 'Request contact details', 'Request booking'];
const ESCALATION = ['WhatsApp', 'Email', 'Call', 'Live Chat', 'Support Ticket'];
const LANGUAGES = ['English', 'Arabic', 'Hindi', 'Urdu', 'Multi-language'];
const TONES = ['Professional', 'Friendly', 'Luxury', 'Corporate', 'Sales Focused', 'Custom'];
interface StepDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  hint: string;
}
const STEPS: StepDef[] = [{
  id: 'identity',
  label: 'Identity',
  icon: <UserIcon className="w-4 h-4" data-id="element-3125" data-mp="1p60259e59wa5" />,
  hint: 'Name your AI employee'
}, {
  id: 'appearance',
  label: 'Appearance',
  icon: <PaintbrushIcon className="w-4 h-4" data-id="element-3126" data-mp="1kd6vgfz48dvf" />,
  hint: 'Match your brand'
}, {
  id: 'welcome',
  label: 'Welcome',
  icon: <MessageSquareIcon className="w-4 h-4" data-id="element-3127" data-mp="wmuekflw76sp" />,
  hint: 'First impression'
}, {
  id: 'knowledge',
  label: 'Knowledge',
  icon: <DatabaseIcon className="w-4 h-4" data-id="element-3128" data-mp="1hergw59e941y" />,
  hint: 'What it can access'
}, {
  id: 'leads',
  label: 'Leads',
  icon: <UserPlusIcon className="w-4 h-4" data-id="element-3129" data-mp="1leigmygweuim" />,
  hint: 'Capture & qualify'
}, {
  id: 'behavior',
  label: 'Behavior',
  icon: <BrainIcon className="w-4 h-4" data-id="element-3130" data-mp="1q8mp5m5tbalt" />,
  hint: 'Teach the rules'
}, {
  id: 'test',
  label: 'Test',
  icon: <PlayIcon className="w-4 h-4" data-id="element-3131" data-mp="yfdmjm7r5rax" />,
  hint: 'Try it live'
}, {
  id: 'deploy',
  label: 'Deploy',
  icon: <RocketIcon className="w-4 h-4" data-id="element-3132" data-mp="12gfmy6deo9eh" />,
  hint: 'Go live'
}];
export function WidgetBuilder({
  onBack,
  onDeploy,
  initialStep
}: {
  onBack: () => void;
  onDeploy: () => void;
  initialStep?: string;
}) {
  const [stepIdx, setStepIdx] = useState(() => {
    const i = STEPS.findIndex(s => s.id === initialStep);
    return i >= 0 ? i : 0;
  });
  const step = STEPS[stepIdx].id;
  const showPreview = !['test', 'deploy'].includes(step);
  const [config, setConfig] = useState<WidgetConfig>({
    name: 'Talab Assistant',
    description: 'Helping customers find vehicles and answer questions.',
    greeting: "Welcome to Al Futtaim Motors 👋 I'm here to help you find the perfect vehicle.",
    prompts: ['Find me a Prado under AED 90,000', 'Do you offer delivery?', 'Can I book a viewing?', 'What financing options are available?'],
    primaryColor: '#4338CA',
    accentColor: '#A855F7',
    theme: 'light',
    position: 'Bottom Right',
    size: 'Standard',
    avatar: 'T',
    tone: 'Friendly'
  });
  const set = (patch: Partial<WidgetConfig>) => setConfig(c => ({
    ...c,
    ...patch
  }));
  const [knowledge, setKnowledge] = useState<string[]>(['Inventory', 'Catalog', 'FAQs', 'Pricing Rules']);
  const [leadFields, setLeadFields] = useState<string[]>(['Name', 'Mobile Number', 'Budget']);
  const [triggers, setTriggers] = useState<string[]>(['Request quotation', 'Request viewing']);
  const [escalation, setEscalation] = useState<string[]>(['WhatsApp']);
  const [languages, setLanguages] = useState<string[]>(['English', 'Arabic']);
  const [behavior, setBehavior] = useState('If customer asks about delivery inside Dubai, add AED 50.\nIf customer asks about airport pickup, add AED 75.\nAlways suggest GCC vehicles first.\nOffer financing when budget exceeds AED 80,000.');
  const toggle = (arr: string[], setArr: (v: string[]) => void, v: string) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const isLast = stepIdx === STEPS.length - 1;
  const next = () => setStepIdx(i => Math.min(STEPS.length - 1, i + 1));
  const prev = () => setStepIdx(i => Math.max(0, i - 1));
  return <div className="bg-white border border-slate-200 rounded-3xl shadow-soft overflow-hidden" data-id="element-3133" data-mp="t8ugkt6xsf5r">
      {/* Builder top bar */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3" data-id="element-3134" data-mp="fao0ms1y0zud">
        <div className="flex items-center gap-3 min-w-0" data-id="element-3135" data-mp="jbzkegy4yqun">
          <button onClick={onBack} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg flex-shrink-0" data-id="element-3136" data-mp="mnqc6ftwend">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-3137" data-mp="9maq6nswtceb" />
          </button>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center flex-shrink-0" data-id="element-3138" data-mp="9kg6tdxadscc">
            <SparklesIcon className="w-5 h-5 text-white" data-id="element-3139" data-mp="jjx8zldbxo3k" />
          </div>
          <div className="min-w-0" data-id="element-3140" data-mp="elni3myn19nr">
            <div className="font-display font-bold text-dark-navy truncate" data-id="element-3141" data-mp="ekkrlg58998d">
              {config.name}
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1.5" data-id="element-3142" data-mp="sc9079g9pfkl">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-3143" data-mp="ocqg2fw9t8it" /> Draft ·
              Website Widget
            </div>
          </div>
        </div>
        <button onClick={onDeploy} className="px-5 py-2 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 flex items-center gap-2 flex-shrink-0" data-id="element-3144" data-mp="xc57bhk9hn4d">
          <RocketIcon className="w-4 h-4" data-id="element-3145" data-mp="3v5f7g44kncx0" /> Deploy
        </button>
      </div>

      {/* Internal step nav bar */}
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60" data-id="element-3146" data-mp="3yvqs8c20sqs">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-id="element-3147" data-mp="8q3ymzxqk61u">
          {STEPS.map((s, i) => {
          const active = i === stepIdx;
          const done = i < stepIdx;
          return <Fragment key={s.id} data-id="element-3148" data-mp="ins3wjsywvy9" data-mpi={i}>
                <button onClick={() => setStepIdx(i)} className={`flex items-center gap-2 px-3 py-2 rounded-xl whitespace-nowrap transition-colors flex-shrink-0 ${active ? 'bg-white shadow-sm border border-slate-200' : 'hover:bg-white/60'}`} data-id="element-3149" data-mp="imqgdfurmgzn">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-green-100 text-green-600' : active ? 'bg-primary-indigo text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-3150" data-mp="2oit4oua7gazq">
                    {done ? <CheckIcon className="w-3.5 h-3.5" data-id="element-3151" data-mp="2h5tv7wiat861" /> : i + 1}
                  </span>
                  <span className={`text-sm font-medium ${active ? 'text-dark-navy' : done ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-3152" data-mp="2c6hc9mujsl8q">
                    {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && <span className="w-4 h-px bg-slate-200 flex-shrink-0" data-id="element-3153" data-mp="2zw39w5v9usem" />}
              </Fragment>;
        })}
        </div>
      </div>

      {/* Body: focused step + live preview */}
      <div className="flex flex-col lg:flex-row" data-id="element-3154" data-mp="2qwohv1zgi9s0">
        <div className={`flex-1 p-6 lg:p-8 ${showPreview ? '' : 'max-w-3xl mx-auto w-full'}`} data-id="element-3155" data-mp="2qxzmt54n1nkp">
          <div className="mb-6" data-id="element-3156" data-mp="1fgriejtzs7gj">
            <div className="flex items-center gap-2 text-primary-indigo mb-1" data-id="element-3157" data-mp="1fgi0ljj3p6lx">
              {STEPS[stepIdx].icon}
              <span className="text-xs font-bold uppercase tracking-wider" data-id="element-3158" data-mp="3ekqamk6nm96s">
                Step {stepIdx + 1} of {STEPS.length}
              </span>
            </div>
            <h3 className="text-2xl font-display font-bold text-dark-navy" data-id="element-3159" data-mp="35lcxnhjbn2hb">
              {STEPS[stepIdx].label}
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-3160" data-mp="3akpla4i0odky">{STEPS[stepIdx].hint}</p>
          </div>

          <AnimatePresence mode="wait" data-id="element-3161" data-mp="255v5ecn7ba4">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} className="space-y-5" data-id="element-3162" data-mp="267fk9fjta4i">
              {step === 'identity' && <>
                  <Field label="Agent name" value={config.name} onChange={v => set({
                name: v
              })} placeholder="Talab Assistant" data-id="element-3163" data-mp="pu74r4x7osgz" />
                  <Field label="Description" value={config.description} onChange={v => set({
                description: v
              })} textarea placeholder="What does this agent help with?" data-id="element-3164" data-mp="18x94bwrwfun5" />
                  <div data-id="element-3165" data-mp="q8c99qoogy3v">
                    <Lbl data-id="element-3166" data-mp="q8lw55efep5c">Avatar</Lbl>
                    <div className="flex items-center gap-3" data-id="element-3167" data-mp="q5g8lulg3vbq">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold" style={{
                    backgroundColor: config.primaryColor
                  }} data-id="element-3168" data-mp="l7yaz0cvp2j1">
                        {config.avatar || 'T'}
                      </div>
                      <Upload label="" data-id="element-3169" data-mp="187yin8ubcdh" />
                    </div>
                  </div>
                </>}

              {step === 'appearance' && <>
                  <div className="grid grid-cols-2 gap-4" data-id="element-3170" data-mp="3noltxn5ytzir">
                    <Color label="Primary color" value={config.primaryColor} onChange={v => set({
                  primaryColor: v
                })} data-id="element-3171" data-mp="3no2f4fbm3r8y" />
                    <Color label="Accent color" value={config.accentColor} onChange={v => set({
                  accentColor: v
                })} data-id="element-3172" data-mp="39qnl0xaeyvan" />
                  </div>
                  <Segment label="Theme" options={['light', 'dark']} value={config.theme} onChange={v => set({
                theme: v as WidgetConfig['theme']
              })} data-id="element-3173" data-mp="1bfdmd2p4zb1e" />
                  <Segment label="Position" options={POSITIONS} value={config.position} onChange={v => set({
                position: v as WidgetConfig['position']
              })} data-id="element-3174" data-mp="1p57cdocdo1cp" />
                  <Segment label="Size" options={SIZES} value={config.size} onChange={v => set({
                size: v as WidgetConfig['size']
              })} data-id="element-3175" data-mp="11jainsemz4cc" />
                </>}

              {step === 'welcome' && <>
                  <Field label="Greeting message" value={config.greeting} onChange={v => set({
                greeting: v
              })} textarea data-id="element-3176" data-mp="1n8h2otl85kex" />
                  <PromptEditor prompts={config.prompts} onChange={prompts => set({
                prompts
              })} data-id="element-3177" data-mp="19frh2q9lyhic" />
                </>}

              {step === 'knowledge' && <div data-id="element-3178" data-mp="1ee2pcwk98jyg">
                  <Lbl data-id="element-3179" data-mp="1edklpev9j3gi">Let your agent answer using</Lbl>
                  <ChipGroup options={KNOWLEDGE} selected={knowledge} onToggle={v => toggle(knowledge, setKnowledge, v)} data-id="element-3180" data-mp="qp1bgtsnkgls" />
                </div>}

              {step === 'leads' && <>
                  <div data-id="element-3181" data-mp="3263hzx26f587">
                    <Lbl data-id="element-3182" data-mp="326msisphyrc7">Collect from customers</Lbl>
                    <ChipGroup options={LEAD_FIELDS} selected={leadFields} onToggle={v => toggle(leadFields, setLeadFields, v)} data-id="element-3183" data-mp="2ngntknq4bbvo" />
                  </div>
                  <div data-id="element-3184" data-mp="3g5ckxv1dg2u8">
                    <Lbl className="mt-2" data-id="element-3185" data-mp="3g3079u2ncemz">
                      Create a lead when customer asks to
                    </Lbl>
                    <ChipGroup options={TRIGGERS} selected={triggers} onToggle={v => toggle(triggers, setTriggers, v)} data-id="element-3186" data-mp="374epi15013n9" />
                  </div>
                </>}

              {step === 'behavior' && <>
                  <div className="rounded-2xl p-4 border border-ai-purple/30 bg-purple-50/40" data-id="element-3187" data-mp="1zrmjyx5jtuot">
                    <div className="flex items-center gap-2 mb-2" data-id="element-3188" data-mp="2fyr9kmareba6">
                      <BrainIcon className="w-4 h-4 text-ai-purple" data-id="element-3189" data-mp="2b798ly8qkap7" />
                      <span className="font-bold text-dark-navy text-sm" data-id="element-3190" data-mp="2b4wkf3cdx43p">
                        Teach your agent — in plain language
                      </span>
                    </div>
                    <textarea value={behavior} onChange={e => setBehavior(e.target.value)} rows={5} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20 resize-none" data-id="element-3191" data-mp="21amojbo8hno8" />
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-3192" data-mp="2kxc01u07lglf">
                      {behavior.split('\n').filter(Boolean).map((_, i) => <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white text-ai-purple text-xs font-medium rounded-full border border-purple-100" data-id="element-3193" data-mp="19gklstlq0d46" data-mpi={i}>
                            <CheckIcon className="w-3 h-3" data-id="element-3194" data-mp="3u5npd8sbiw47" /> Rule {i + 1}{' '}
                            structured
                          </span>)}
                    </div>
                  </div>
                  <div data-id="element-3195" data-mp="7qvwjfrko3kf">
                    <Lbl className="flex items-center gap-1.5" data-id="element-3196" data-mp="7pb2dr966mn3">
                      <HeadphonesIcon className="w-3.5 h-3.5" data-id="element-3197" data-mp="conyz3puf82v" /> Escalate to a
                      human via
                    </Lbl>
                    <ChipGroup options={ESCALATION} selected={escalation} onToggle={v => toggle(escalation, setEscalation, v)} data-id="element-3198" data-mp="gs2dfpy4vge7" />
                  </div>
                  <div data-id="element-3199" data-mp="2iki64e24naly">
                    <Lbl className="flex items-center gap-1.5" data-id="element-3200" data-mp="2ngp6lrbr7twi">
                      <LanguagesIcon className="w-3.5 h-3.5" data-id="element-3201" data-mp="2nj1xitzhpelp" /> Languages
                    </Lbl>
                    <ChipGroup options={LANGUAGES} selected={languages} onToggle={v => toggle(languages, setLanguages, v)} data-id="element-3202" data-mp="3qtz9kvd4nkii" />
                  </div>
                  <Segment label="Tone" options={TONES} value={config.tone} onChange={v => set({
                tone: v
              })} data-id="element-3203" data-mp="3ut825wwflcch" />
                </>}

              {step === 'test' && <CustomerPreview config={config} data-id="element-3204" data-mp="mg0r9c5fk6b0" />}
              {step === 'deploy' && <DeployView config={config} data-id="element-3205" data-mp="idm8st5ob8k9" />}
            </motion.div>
          </AnimatePresence>

          {/* Footer nav */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100" data-id="element-3206" data-mp="w9zru6puvi0u">
            <button onClick={prev} disabled={stepIdx === 0} className={`px-5 py-2.5 font-medium rounded-xl transition-colors flex items-center gap-2 ${stepIdx === 0 ? 'opacity-0 pointer-events-none' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-3207" data-mp="rawm0k21x7rt">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-3208" data-mp="112ppzbruyfgc" /> Back
            </button>
            {isLast ? <button onClick={onDeploy} className="px-6 py-2.5 bg-primary-indigo text-white font-bold rounded-xl hover:bg-indigo-700 flex items-center gap-2 shadow-md" data-id="element-3209" data-mp="161swz1reevqy">
                <RocketIcon className="w-4 h-4" data-id="element-3210" data-mp="ia5d33sk0ky6" /> Deploy Agent
              </button> : <button onClick={next} className="px-6 py-2.5 bg-dark-navy text-white font-medium rounded-xl hover:bg-slate-800 flex items-center gap-2 shadow-md" data-id="element-3211" data-mp="n988rxmn9eth">
                Continue <ArrowRightIcon className="w-4 h-4" data-id="element-3212" data-mp="dbka4wlx3n61" />
              </button>}
          </div>
        </div>

        {/* Live preview */}
        {showPreview && <div className="hidden lg:flex flex-col w-[400px] flex-shrink-0 border-l border-slate-100 bg-slate-100 relative" data-id="element-3213" data-mp="orgv030qt6kk">
            <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-white/80 backdrop-blur rounded-full text-xs font-medium text-slate-500 flex items-center gap-1.5 z-10 shadow-sm" data-id="element-3214" data-mp="jruffmxfze8l">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-3215" data-mp="as7hxc7u2skt" />{' '}
              Live Preview
            </div>
            <div className="flex-1 p-6 pt-16 overflow-hidden bg-[radial-gradient(circle_at_center,#e2e8f0_1px,transparent_1px)] [background-size:18px_18px]" data-id="element-3216" data-mp="5zebmjwpvc6m">
              <WidgetPreview config={config} data-id="element-3217" data-mp="3s8paig7zzuc4" />
            </div>
          </div>}
      </div>
    </div>;
}
/* ---------- Customer Preview ---------- */
function CustomerPreview({
  config
}: {
  config: WidgetConfig;
}) {
  const [msgs, setMsgs] = useState<{
    role: 'c' | 'a';
    t: string;
  }[]>([{
    role: 'a',
    t: config.greeting
  }]);
  const [input, setInput] = useState('');
  const reply = (q: string) => {
    if (!q.trim()) return;
    const l = q.toLowerCase();
    let a = 'I can help with that. Could you share a few more details?';
    if (l.includes('deliver')) a = 'Yes. Delivery inside Dubai is AED 50.';else if (l.includes('financ')) a = 'Yes. Financing options are available for vehicles above AED 80,000.';else if (l.includes('airport')) a = 'Airport pickup is available for AED 75.';else if (l.includes('prado')) a = 'I found 3 GCC Prado vehicles under AED 90,000. Want to see them?';
    setMsgs(m => [...m, {
      role: 'c',
      t: q
    }, {
      role: 'a',
      t: a
    }]);
    setInput('');
  };
  return <div className="max-w-lg mx-auto w-full bg-white border border-slate-200 rounded-2xl shadow-soft flex flex-col overflow-hidden h-[420px]" data-id="element-3218" data-mp="wagcu48k2q1t">
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-3219" data-mp="97vnju57ata5">
        {msgs.map((m, i) => <div key={i} className={`flex ${m.role === 'c' ? 'justify-end' : 'gap-2'}`} data-id="element-3220" data-mp="e6q22jw4xvog" data-mpi={i}>
            {m.role === 'a' && <div className="w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{
          backgroundColor: config.primaryColor
        }} data-id="element-3221" data-mp="j6cfoufvldxs">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-3222" data-mp="3rlmi63nogl63" />
              </div>}
            <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${m.role === 'c' ? 'text-white rounded-tr-sm' : 'bg-slate-100 text-slate-800 rounded-tl-sm'}`} style={m.role === 'c' ? {
          backgroundColor: config.primaryColor
        } : {}} data-id="element-3223" data-mp="3rj9xvjug5ypv">
              {m.t}
            </div>
          </div>)}
      </div>
      <div className="p-3 border-t border-slate-100 flex items-center gap-2" data-id="element-3224" data-mp="1pzh1hlrsqrun">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && reply(input)} placeholder="Test the agent…" className="flex-1 px-3 py-2 bg-slate-100 rounded-full text-sm outline-none" data-id="element-3225" data-mp="1lzgo84gfomp6" />
        <button onClick={() => reply(input)} className="w-9 h-9 rounded-full text-white flex items-center justify-center flex-shrink-0" style={{
        backgroundColor: config.primaryColor
      }} data-id="element-3226" data-mp="171eetdmq1700">
          <SendIcon className="w-4 h-4" data-id="element-3227" data-mp="y6qf02zx0hil" />
        </button>
      </div>
    </div>;
}
/* ---------- Deploy ---------- */
function DeployView({
  config
}: {
  config: WidgetConfig;
}) {
  const [copied, setCopied] = useState(false);
  const snippet = `<script src="https://cdn.talab.ai/widget.js"\n  data-agent="${config.name.toLowerCase().replace(/\s+/g, '-')}"\n  data-key="pk_live_8f2a..."\n  async>\n</script>`;
  return <div className="space-y-6" data-id="element-3228" data-mp="en19trcq73lm">
      <div className="bg-slate-900 rounded-2xl p-5 relative" data-id="element-3229" data-mp="enaxh8aasyo5">
        <button onClick={() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }} className="absolute top-4 right-4 px-3 py-1.5 bg-white/10 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-white/20" data-id="element-3230" data-mp="n9of5jvqkxl">
          {copied ? <>
              <CheckIcon className="w-3.5 h-3.5" data-id="element-3231" data-mp="xchdgnjpjc69" /> Copied
            </> : <>
              <CopyIcon className="w-3.5 h-3.5" data-id="element-3232" data-mp="jlck236xng4d" /> Copy
            </>}
        </button>
        <pre className="text-sm text-green-300 font-mono whitespace-pre-wrap leading-relaxed" data-id="element-3233" data-mp="nmekgncl5y7x">
          {snippet}
        </pre>
      </div>
      <div data-id="element-3234" data-mp="1bbgzrojqzez9">
        <Lbl data-id="element-3235" data-mp="1g40cykeiw83j">One-click installs</Lbl>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-3236" data-mp="1g51yxjx2uf01">
          {['WordPress', 'Shopify', 'Wix', 'Webflow', 'Custom Website'].map(p => <button key={p} className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-dark-navy hover:border-primary-indigo/40 shadow-soft text-left" data-id="element-3237" data-mp="2vvm32t8owt3z">
                {p}
              </button>)}
        </div>
      </div>
      <div data-id="element-3238" data-mp="2hurv2bpk0u5u">
        <Lbl data-id="element-3239" data-mp="2hwcnnvsb6312">Allowed domains</Lbl>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2 shadow-soft" data-id="element-3240" data-mp="2hxns7xtkvet8">
          {['example.com', 'www.example.com', 'shop.example.com'].map(d => <div key={d} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg text-sm" data-id="element-3241" data-mp="2rvbx3gkwe2p4">
              <span className="text-slate-700 font-mono" data-id="element-3242" data-mp="730e7dwiijiy">{d}</span>
              <XIcon className="w-4 h-4 text-slate-300 hover:text-red-400 cursor-pointer" data-id="element-3243" data-mp="75mhv2ga8evw" />
            </div>)}
          <button className="flex items-center gap-1.5 text-sm text-primary-indigo font-medium px-3 py-1.5" data-id="element-3244" data-mp="27sz7z8ur9gw6">
            <PlusIcon className="w-4 h-4" data-id="element-3245" data-mp="2crahfih51pdv" /> Add domain
          </button>
        </div>
      </div>
    </div>;
}
/* ---------- Controls ---------- */
function Lbl({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <label className={`block text-sm font-medium text-slate-700 mb-2 ${className}`} data-id="element-3246" data-mp="2lqe2grzv8ev0">
      {children}
    </label>;
}
function Field({
  label,
  value,
  onChange,
  textarea,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
  placeholder?: string;
}) {
  return <div data-id="element-3247" data-mp="34jhzmncal9ud">
      <Lbl data-id="element-3248" data-mp="34j88uxjfaynm">{label}</Lbl>
      {textarea ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} placeholder={placeholder} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20 resize-none" data-id="element-3249" data-mp="34l2teaqs8or6" /> : <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-3250" data-mp="3jlzv1rnzo84w" />}
    </div>;
}
function Color({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return <div data-id="element-3251" data-mp="66tsuveivvmu">
      <Lbl data-id="element-3252" data-mp="684w8u4231df">{label}</Lbl>
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5" data-id="element-3253" data-mp="67n0rzp7wr6k">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" data-id="element-3254" data-mp="1ekac0fqlxul" />
        <span className="text-sm text-slate-600 font-mono" data-id="element-3255" data-mp="tysiylimrvk1">{value}</span>
      </div>
    </div>;
}
function Upload({
  label
}: {
  label: string;
}) {
  return <button className="flex-1 border-2 border-dashed border-slate-200 rounded-xl py-4 flex flex-col items-center gap-1 text-slate-400 hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors" data-id="element-3256" data-mp="2jdjrfbn2a60e">
      <UploadIcon className="w-5 h-5" data-id="element-3257" data-mp="29n8jzf465eor" />
      <span className="text-xs" data-id="element-3258" data-mp="29m5tv9eo7g9w">Upload {label}</span>
    </button>;
}
function Segment({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return <div data-id="element-3259" data-mp="25molo87v0511">
      <Lbl data-id="element-3260" data-mp="25n6hj3qm0h8f">{label}</Lbl>
      <div className="flex flex-wrap gap-2" data-id="element-3261" data-mp="25nq10ovwrdok">
        {options.map(o => <button key={o} onClick={() => onChange(o)} className={`px-4 py-2 text-sm font-medium rounded-xl border transition-colors capitalize ${value === o ? 'bg-primary-indigo text-white border-primary-indigo' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-3262" data-mp="1qqfpjfj9m7su">
            {o}
          </button>)}
      </div>
    </div>;
}
function ChipGroup({
  options,
  selected,
  onToggle
}: {
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return <div className="flex flex-wrap gap-2" data-id="element-3263" data-mp="1o4pxww6o140h">
      {options.map(o => {
      const on = selected.includes(o);
      return <button key={o} onClick={() => onToggle(o)} className={`px-3.5 py-2 text-sm font-medium rounded-xl border transition-colors flex items-center gap-1.5 ${on ? 'bg-indigo-50 text-primary-indigo border-primary-indigo/30' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-3264" data-mp="1jcydl3e3ruis">
            {on && <CheckIcon className="w-3.5 h-3.5" data-id="element-3265" data-mp="2717ups0dfu3l" />} {o}
          </button>;
    })}
    </div>;
}
function PromptEditor({
  prompts,
  onChange
}: {
  prompts: string[];
  onChange: (p: string[]) => void;
}) {
  return <div data-id="element-3266" data-mp="7qzorb0ovpjz">
      <Lbl data-id="element-3267" data-mp="7r9hv7h16ctw">Suggested prompts</Lbl>
      <div className="space-y-2" data-id="element-3268" data-mp="7sasjau4wxb3">
        {prompts.map((p, i) => <div key={i} className="flex items-center gap-2" data-id="element-3269" data-mp="3i1n845akqhbf" data-mpi={i}>
            <input value={p} onChange={e => onChange(prompts.map((x, j) => j === i ? e.target.value : x))} className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-3270" data-mp="3n1rqk0v1lm4a" />
            <button onClick={() => onChange(prompts.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-400" data-id="element-3271" data-mp="2z4m0sdtjs54b">
              <XIcon className="w-4 h-4" data-id="element-3272" data-mp="39206bidxn9hu" />
            </button>
          </div>)}
        <button onClick={() => onChange([...prompts, ''])} className="flex items-center gap-1.5 text-sm text-primary-indigo font-medium" data-id="element-3273" data-mp="394cwujxras6i">
          <PlusIcon className="w-4 h-4" data-id="element-3274" data-mp="ob9egvcnkr4s" /> Add prompt
        </button>
      </div>
    </div>;
}